/**
 * Constants for the Condition Builder component.
 * Defines available fields, operators, and their configurations.
 */

import i18n from "@tellescope/i18n";
import type { CustomEnduserField } from "@tellescope/types-models";
import type { DataSourceType } from "../../../shared/schema/visibility";

// Field type definitions
export type FieldType = "string" | "number" | "array" | "date" | "boolean";

// Field option for predefined value dropdowns
export interface FieldOption {
  value: string;
  label: string;
}

// Field definition
export interface FieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  description?: string;
  /** If set, only these operators are available for this field */
  allowedOperators?: string[];
  /** If set, value input becomes a dropdown with these options */
  options?: FieldOption[];
  /**
   * If set, this field requires a suffix key input.
   * The final field path will be `{id}.{userEnteredKey}`.
   * Used for metadata fields where the user specifies the key name.
   */
  suffixKeyInput?: {
    /** Placeholder text for the key input */
    placeholder: string;
  };
}

// Operator definition
export interface OperatorDefinition {
  id: string;
  label: string;
  requiresValue: boolean;
  valueType?: "text" | "number" | "select" | "multiselect" | "date" | "boolean";
}

// Data source definition
export interface DataSourceDefinition {
  id: DataSourceType;
  label: string;
  singularLabel: string;
  description: string;
  fields: FieldDefinition[];
}

/**
 * Enduser fields available for visibility conditions.
 * Phase 1: Only enduser fields are supported.
 */
export const getEnduserFields: () => FieldDefinition[] = () => [
  // Basic profile
  { id: "fname", label: i18n.t("First Name", { ns: "common" }), type: "string" },
  { id: "lname", label: i18n.t("Last Name", { ns: "common" }), type: "string" },
  { id: "email", label: i18n.t("Email", { ns: "common" }), type: "string" },
  { id: "phone", label: i18n.t("Phone", { ns: "common" }), type: "string" },
  { id: "dateOfBirth", label: i18n.t("Date of Birth", { ns: "common" }), type: "date" },
  { id: "gender", label: i18n.t("Gender", { ns: "common" }), type: "string" },

  // Location
  { id: "state", label: i18n.t("State", { ns: "common" }), type: "string" },
  { id: "city", label: i18n.t("City", { ns: "common" }), type: "string" },
  { id: "zipCode", label: i18n.t("Zip Code", { ns: "common" }), type: "string" },
  { id: "addressLineOne", label: i18n.t("Address Line 1", { ns: "common" }), type: "string" },
  { id: "addressLineTwo", label: i18n.t("Address Line 2", { ns: "common" }), type: "string" },

  // Arrays
  { id: "tags", label: i18n.t("Tags", { ns: "common" }), type: "array", description: "Patient tags" },
  { id: "assignedTo", label: i18n.t("Assigned To", { ns: "common" }), type: "array", description: "Assigned user IDs" },
  {
    id: "$statuses",
    label: i18n.t("Journey Status", { ns: "common" }),
    type: "array",
    description: "Patient journey statuses",
  },

  // Nested fields
  { id: "insurance.payerName", label: i18n.t("Insurance Payer", { ns: "common" }), type: "string" },
  { id: "insurance.memberId", label: i18n.t("Insurance Member ID", { ns: "common" }), type: "string" },
  { id: "insurance.relationship", label: i18n.t("Insurance Relationship", { ns: "common" }), type: "string" },

  // IDs
  {
    id: "customTypeId",
    label: i18n.t("Contact Type", { ns: "common" }),
    type: "string",
    description: "Contact type ID",
  },
  { id: "journeyId", label: i18n.t("Journey", { ns: "common" }), type: "array", description: "Journey IDs" },
];

/**
 * Form response fields available for visibility conditions.
 * Uses "any match" semantics - condition is true if ANY form response matches.
 */
export const getFormResponseFields: () => FieldDefinition[] = () => [
  {
    id: "formTitle",
    label: i18n.t("Form Title", { ns: "common" }),
    type: "string",
    description: "Title of the submitted form",
  },
  {
    id: "submittedAt",
    label: i18n.t("Submitted Date", { ns: "common" }),
    type: "date",
    description: "Date when the form was submitted (null if not yet submitted)",
  },
];

/**
 * Stripe subscription status options.
 * Based on Stripe API subscription status values.
 */
export const getStripeSubscriptionStatusOptions: () => FieldOption[] = () => [
  { value: "active", label: i18n.t("Active", { ns: "common" }) },
  { value: "canceled", label: i18n.t("Canceled", { ns: "common" }) },
  { value: "incomplete", label: i18n.t("Incomplete", { ns: "common" }) },
  { value: "incomplete_expired", label: i18n.t("Incomplete Expired", { ns: "common" }) },
  { value: "past_due", label: i18n.t("Past Due", { ns: "common" }) },
  { value: "paused", label: i18n.t("Paused", { ns: "common" }) },
  { value: "trialing", label: i18n.t("Trialing", { ns: "common" }) },
  { value: "unpaid", label: i18n.t("Unpaid", { ns: "common" }) },
];

export const getStripeInvoiceStatusOptions: () => FieldOption[] = () => [
  { value: "paid", label: i18n.t("Paid", { ns: "payments" }) },
  { value: "open", label: i18n.t("Open", { ns: "payments" }) },
  { value: "draft", label: i18n.t("Draft", { ns: "payments" }) },
  { value: "void", label: i18n.t("Void", { ns: "payments" }) },
  { value: "uncollectible", label: i18n.t("Uncollectible", { ns: "payments" }) },
];

export const getStripeInvoiceFields: () => FieldDefinition[] = () => [
  {
    id: "status",
    label: i18n.t("Status", { ns: "common" }),
    type: "string",
    description: "Invoice status",
    allowedOperators: ["$eq"],
    options: getStripeInvoiceStatusOptions(),
  },
  {
    id: "amount_due",
    label: i18n.t("Amount Due", { ns: "payments" }),
    type: "number",
    description: "Amount due in cents",
  },
  {
    id: "amount_paid",
    label: i18n.t("Amount Paid", { ns: "payments" }),
    type: "number",
    description: "Amount paid in cents",
  },
  {
    id: "amount_remaining",
    label: i18n.t("Amount Remaining", { ns: "payments" }),
    type: "number",
    description: "Amount remaining in cents",
  },
  {
    id: "currency",
    label: i18n.t("Currency", { ns: "payments" }),
    type: "string",
    description: "Invoice currency code (e.g., usd)",
  },
  {
    id: "created",
    label: i18n.t("Created", { ns: "common" }),
    type: "date",
    description: "Invoice creation date",
  },
];

/**
 * Stripe subscription fields available for visibility conditions.
 * Uses "any match" semantics - condition is true if ANY subscription matches.
 */
export const getStripeSubscriptionFields: () => FieldDefinition[] = () => [
  {
    id: "status",
    label: i18n.t("Status", { ns: "common" }),
    type: "string",
    description: "Subscription status",
    allowedOperators: ["$eq"],
    options: getStripeSubscriptionStatusOptions(),
  },
  {
    id: "productName",
    label: i18n.t("Product Name", { ns: "common" }),
    type: "string",
    description: "Name of the subscribed product",
  },
  {
    id: "productMetadata",
    label: i18n.t("Product Metadata", { ns: "common" }),
    type: "string",
    description: "Product metadata key-value pair from Stripe",
    suffixKeyInput: {
      placeholder: "e.g., tier, plan_type",
    },
  },
];

const getCreatedAtField = (): FieldDefinition => ({
  id: "createdAt",
  label: i18n.t("Created At", { ns: "common" }),
  type: "date",
  description: "Creation timestamp",
});
const getUpdatedAtField = (): FieldDefinition => ({
  id: "updatedAt",
  label: i18n.t("Updated At", { ns: "common" }),
  type: "date",
  description: "Last update timestamp",
});
const getSourceField = (): FieldDefinition => ({
  id: "source",
  label: i18n.t("Source", { ns: "common" }),
  type: "string",
  description: "Source",
});

/**
 * Form fields available for visibility conditions.
 * Used for item filtering in data blocks (e.g., filtering pinned forms).
 * Represents the current form being evaluated.
 */
export const getFormFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Form title",
  },
  {
    id: "displayTitle",
    label: i18n.t("Display Title", { ns: "common" }),
    type: "string",
    description: "Display title (if set)",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
];

/**
 * Appointment status options derived from EventStatus interface.
 */
export const getAppointmentStatusOptions: () => FieldOption[] = () => [
  { value: "upcoming", label: i18n.t("Upcoming", { ns: "common" }) },
  { value: "past", label: i18n.t("Past", { ns: "common" }) },
  { value: "cancelled", label: i18n.t("Cancelled", { ns: "common" }) },
  { value: "rescheduled", label: i18n.t("Rescheduled", { ns: "common" }) },
];

/**
 * Appointment (CalendarEvent) fields for item filtering.
 */
export const getAppointmentFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Event title",
  },
  {
    id: "calendarEventTemplateId",
    label: i18n.t("Template ID", { ns: "common" }),
    type: "string",
    description: "Calendar event template ID",
  },
  {
    id: "startTimeInMS",
    label: i18n.t("Start Timestamp", { ns: "common" }),
    type: "date",
    description: "Event start time",
  },
  {
    id: "$status",
    label: i18n.t("Status", { ns: "common" }),
    type: "string",
    description: "Computed event status",
    allowedOperators: ["$eq"],
    options: getAppointmentStatusOptions(),
  },
  {
    id: "cancelledAt",
    label: i18n.t("Cancelled At", { ns: "common" }),
    type: "date",
    description: "Cancellation timestamp (if cancelled)",
  },
  {
    id: "rescheduledAt",
    label: i18n.t("Rescheduled At", { ns: "common" }),
    type: "date",
    description: "Rescheduled timestamp (if rescheduled)",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * Order (EnduserOrder) fields for item filtering.
 */
export const getOrderFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Order title",
  },
  {
    id: "status",
    label: i18n.t("Status", { ns: "common" }),
    type: "string",
    description: "Order status",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * ChatRoom fields for item filtering.
 */
export const getChatRoomFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Chat room title",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * File fields for item filtering.
 */
export const getFileFields: () => FieldDefinition[] = () => [
  {
    id: "name",
    label: i18n.t("Name", { ns: "common" }),
    type: "string",
    description: "File name",
  },
  {
    id: "type",
    label: i18n.t("MIME Type", { ns: "common" }),
    type: "string",
    description: "File MIME type (e.g., image/png, application/pdf)",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * Content (ManagedContentRecord) fields for item filtering.
 */
export const getContentFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Content title",
  },
  {
    id: "type",
    label: i18n.t("Type", { ns: "common" }),
    type: "string",
    description: "Content type (Article, Video, PDF)",
  },
  {
    id: "category",
    label: i18n.t("Category", { ns: "common" }),
    type: "string",
    description: "Content category",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * Purchase fields for item filtering.
 */
export const getPurchaseFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Purchase title/description",
  },
  {
    id: "cost.amount",
    label: i18n.t("Amount (in cents)", { ns: "common" }),
    type: "number",
    description: "Purchase amount (in cents)",
  },
  {
    id: "processor",
    label: i18n.t("Processor", { ns: "common" }),
    type: "string",
    description: "Payment processor (e.g., stripe)",
  },
  {
    id: "description",
    label: i18n.t("Description", { ns: "common" }),
    type: "string",
    description: "Purchase description",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
  getSourceField(),
];

/**
 * Appointment Booking Page fields for item filtering.
 */
export const getAppointmentBookingPageFields: () => FieldDefinition[] = () => [
  {
    id: "title",
    label: i18n.t("Title", { ns: "common" }),
    type: "string",
    description: "Booking page title",
  },
  getCreatedAtField(),
  getUpdatedAtField(),
];

/**
 * Operators available for each field type.
 */
export const getOperatorsByType: () => Record<FieldType, OperatorDefinition[]> = () => ({
  string: [
    { id: "$eq", label: i18n.t("equals", { ns: "common" }), requiresValue: true, valueType: "text" },
    { id: "$ne", label: i18n.t("does not equal", { ns: "common" }), requiresValue: true, valueType: "text" },
    { id: "$in", label: i18n.t("is one of", { ns: "common" }), requiresValue: true, valueType: "multiselect" },
    { id: "$nin", label: i18n.t("is not one of", { ns: "common" }), requiresValue: true, valueType: "multiselect" },
    { id: "$regex", label: i18n.t("matches pattern", { ns: "common" }), requiresValue: true, valueType: "text" },
    { id: "$exists", label: i18n.t("exists", { ns: "common" }), requiresValue: true, valueType: "boolean" },
  ],
  number: [
    { id: "$eq", label: i18n.t("equals", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$ne", label: i18n.t("does not equal", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$gt", label: i18n.t("greater than", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$gte", label: i18n.t("greater than or equal", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$lt", label: i18n.t("less than", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$lte", label: i18n.t("less than or equal", { ns: "common" }), requiresValue: true, valueType: "number" },
    { id: "$in", label: i18n.t("is one of", { ns: "common" }), requiresValue: true, valueType: "multiselect" },
    { id: "$nin", label: i18n.t("is not one of", { ns: "common" }), requiresValue: true, valueType: "multiselect" },
    { id: "$exists", label: i18n.t("exists", { ns: "common" }), requiresValue: true, valueType: "boolean" },
  ],
  array: [
    { id: "$in", label: i18n.t("includes any of", { ns: "common" }), requiresValue: true, valueType: "multiselect" },
    {
      id: "$nin",
      label: i18n.t("does not include any of", { ns: "common" }),
      requiresValue: true,
      valueType: "multiselect",
    },
    { id: "$exists", label: i18n.t("is not empty", { ns: "common" }), requiresValue: true, valueType: "boolean" },
  ],
  date: [
    { id: "$gt", label: i18n.t("after", { ns: "common" }), requiresValue: true, valueType: "date" },
    { id: "$gte", label: i18n.t("on or after", { ns: "common" }), requiresValue: true, valueType: "date" },
    { id: "$lt", label: i18n.t("before", { ns: "common" }), requiresValue: true, valueType: "date" },
    { id: "$lte", label: i18n.t("on or before", { ns: "common" }), requiresValue: true, valueType: "date" },
    { id: "$exists", label: i18n.t("exists", { ns: "common" }), requiresValue: true, valueType: "boolean" },
  ],
  boolean: [
    { id: "$eq", label: i18n.t("is", { ns: "common" }), requiresValue: true, valueType: "boolean" },
    { id: "$exists", label: i18n.t("exists", { ns: "common" }), requiresValue: true, valueType: "boolean" },
  ],
});

/**
 * Data source configurations.
 * Each source represents a data model that can be used for visibility conditions.
 *
 * To add a new source:
 * 1. Add the source ID to the SourceId type above
 * 2. Create a FIELDS array for the source (e.g., ORDER_FIELDS)
 * 3. Add the source configuration to DATA_SOURCES below
 */
export const getDataSources: () => DataSourceDefinition[] = () => [
  {
    id: "enduser",
    label: i18n.t("Contact", { ns: "common" }),
    singularLabel: i18n.t("Contact", { ns: "common" }),
    description: "Patient/Contact data",
    fields: getEnduserFields(),
  },
  {
    id: "formResponse",
    label: i18n.t("Form Responses", { ns: "common" }),
    singularLabel: i18n.t("Form Response", { ns: "common" }),
    description: "Form response data (matches if ANY form response matches)",
    fields: getFormResponseFields(),
  },
  {
    id: "stripeSubscription",
    label: i18n.t("Stripe Subscriptions", { ns: "common" }),
    singularLabel: i18n.t("Stripe Subscription", { ns: "common" }),
    description: "Stripe subscription data (matches if ANY subscription matches)",
    fields: getStripeSubscriptionFields(),
  },
  // Item filter sources - for filtering items within data blocks
  {
    id: "form",
    label: i18n.t("Forms", { ns: "common" }),
    singularLabel: i18n.t("Form", { ns: "common" }),
    description: "Form definition (for item filtering)",
    fields: getFormFields(),
  },
  {
    id: "appointment",
    label: i18n.t("Appointments", { ns: "common" }),
    singularLabel: i18n.t("Appointment", { ns: "common" }),
    description: "Appointment data",
    fields: getAppointmentFields(),
  },
  {
    id: "order",
    label: i18n.t("Orders", { ns: "common" }),
    singularLabel: i18n.t("Order", { ns: "common" }),
    description: "Order data",
    fields: getOrderFields(),
  },
  {
    id: "chatRoom",
    label: i18n.t("Chat Rooms", { ns: "common" }),
    singularLabel: i18n.t("Chat Room", { ns: "common" }),
    description: "Chat room/message thread data",
    fields: getChatRoomFields(),
  },
  {
    id: "file",
    label: i18n.t("Files", { ns: "common" }),
    singularLabel: i18n.t("File", { ns: "common" }),
    description: "File data",
    fields: getFileFields(),
  },
  {
    id: "content",
    label: i18n.t("Content", { ns: "common" }),
    singularLabel: i18n.t("Content Item", { ns: "common" }),
    description: "Managed content record data",
    fields: getContentFields(),
  },
  {
    id: "purchase",
    label: i18n.t("Purchases", { ns: "common" }),
    singularLabel: i18n.t("Purchase", { ns: "common" }),
    description: "Purchase/payment data",
    fields: getPurchaseFields(),
  },
  {
    id: "appointmentBookingPage",
    label: i18n.t("Appointment Booking Pages", { ns: "common" }),
    singularLabel: i18n.t("Appointment Booking Page", { ns: "common" }),
    description: "Booking page data (for item filtering)",
    fields: getAppointmentBookingPageFields(),
  },
  {
    id: "stripeInvoice",
    label: i18n.t("Stripe Invoices", { ns: "common" }),
    singularLabel: i18n.t("Stripe Invoice", { ns: "common" }),
    description: "Stripe invoice data (for item filtering)",
    fields: getStripeInvoiceFields(),
  },
];

/**
 * Maps a CustomEnduserField type to the FieldDefinition type used by the condition builder.
 * Custom field values live at enduser.fields[fieldName], so the field id is `fields.{fieldName}`.
 */
export function buildCustomEnduserFieldDefinitions(customFields: CustomEnduserField[]): FieldDefinition[] {
  const definitions: FieldDefinition[] = [];

  for (const field of customFields) {
    switch (field.type) {
      case "Text":
      case "Auto Detect":
        definitions.push({ id: `fields.${field.field}`, label: field.field, type: "string" });
        break;

      case "Number":
        definitions.push({ id: `fields.${field.field}`, label: field.field, type: "number" });
        break;

      case "Date":
        definitions.push({ id: `fields.${field.field}`, label: field.field, type: "date" });
        break;

      case "Checkbox":
        definitions.push({ id: `fields.${field.field}`, label: field.field, type: "boolean" });
        break;

      case "Select":
        definitions.push({
          id: `fields.${field.field}`,
          label: field.field,
          type: "string",
          options: field.info.options.map((o) => ({ value: o, label: o })),
        });
        break;

      case "Multiple Select":
        definitions.push({
          id: `fields.${field.field}`,
          label: field.field,
          type: "array",
          options: field.info.options.map((o) => ({ value: o, label: o })),
        });
        break;

      case "Multiple Text":
        definitions.push({ id: `fields.${field.field}`, label: field.field, type: "array" });
        break;

      case "Table":
        // Table rows are objects; only existence check makes sense
        definitions.push({
          id: `fields.${field.field}`,
          label: field.field,
          type: "array",
          allowedOperators: ["$exists"],
        });
        break;

      case "File":
      case "Database Select":
        // Only existence check is meaningful: File stores a URL/ID; Database Select
        // stores an opaque record ID — neither has user-readable values to match on.
        definitions.push({
          id: `fields.${field.field}`,
          label: field.field,
          type: "string",
          allowedOperators: ["$exists"],
        });
        break;
    }
  }

  return definitions;
}

/**
 * Get the default source ID.
 */
export function getDefaultSourceId(): string {
  return getDataSources()[0]?.id ?? "enduser";
}

/**
 * Get a data source by ID.
 */
export function getDataSource(sourceId: string): DataSourceDefinition | undefined {
  return getDataSources().find((s) => s.id === sourceId);
}

/**
 * Get fields for a given source.
 */
export function getFieldsForSource(sourceId: string): FieldDefinition[] {
  return getDataSource(sourceId)?.fields ?? [];
}

/**
 * Get field definition by ID for a given source.
 * Also handles fields with suffix keys (e.g., `productMetadata.tier` matches `productMetadata`).
 */
export function getFieldDefinition(sourceId: string, fieldId: string): FieldDefinition | undefined {
  const source = getDataSources().find((s) => s.id === sourceId);
  if (!source) return undefined;

  // First try exact match
  const exactMatch = source.fields.find((f) => f.id === fieldId);
  if (exactMatch) return exactMatch;

  // Check if this is a field with a suffix key (e.g., `productMetadata.tier`)
  const baseFieldId = getBaseFieldId(fieldId);
  if (baseFieldId !== fieldId) {
    const baseField = source.fields.find((f) => f.id === baseFieldId);
    // Only return if the base field supports suffix keys
    if (baseField?.suffixKeyInput) return baseField;
  }

  return undefined;
}

/**
 * Gets the base field ID from a field that may have a suffix key.
 * e.g., `productMetadata.tier` -> `productMetadata`
 * e.g., `productName` -> `productName` (no change)
 */
export function getBaseFieldId(fieldId: string): string {
  const dotIndex = fieldId.indexOf(".");
  return dotIndex > 0 ? fieldId.slice(0, dotIndex) : fieldId;
}

/**
 * Gets the suffix key from a field ID, if present.
 * e.g., `productMetadata.tier` -> `tier`
 * e.g., `productName` -> `""` (empty string)
 */
export function getSuffixKey(fieldId: string): string {
  const dotIndex = fieldId.indexOf(".");
  return dotIndex > 0 ? fieldId.slice(dotIndex + 1) : "";
}

/**
 * Creates a field ID with a suffix key.
 * e.g., `productMetadata` + `tier` -> `productMetadata.tier`
 */
export function createFieldIdWithSuffix(baseFieldId: string, suffixKey: string): string {
  return suffixKey ? `${baseFieldId}.${suffixKey}` : baseFieldId;
}

/**
 * Get operators for a field type.
 */
export function getOperatorsForType(type: FieldType): OperatorDefinition[] {
  return getOperatorsByType()[type] ?? [];
}

/**
 * Get operators for a field, respecting field-level restrictions.
 * If fieldDef has allowedOperators, filters to only those.
 * Otherwise returns all operators for the field type.
 */
export function getOperatorsForField(fieldDef: FieldDefinition | undefined): OperatorDefinition[] {
  if (!fieldDef) return [];

  const allOperators = getOperatorsByType()[fieldDef.type] ?? [];

  // If field has operator restrictions, filter to allowed ones
  if (fieldDef.allowedOperators && fieldDef.allowedOperators.length > 0) {
    return allOperators.filter((op) => fieldDef.allowedOperators!.includes(op.id));
  }

  return allOperators;
}

/**
 * Get operator definition by ID for a given field type.
 */
export function getOperatorDefinition(type: FieldType, operatorId: string): OperatorDefinition | undefined {
  return getOperatorsForType(type).find((op) => op.id === operatorId);
}

/**
 * Data sources that are array-based (support $elemMatch per-item evaluation).
 * Excludes singular sources like "enduser" and "form".
 */
export const ARRAY_DATA_SOURCES: DataSourceType[] = [
  "formResponse",
  "stripeSubscription",
  "appointment",
  "appointmentBookingPage",
  "order",
  "chatRoom",
  "file",
  "content",
  "purchase",
  "stripeInvoice",
];
